import {AfterViewInit, Component, Inject} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {PeoApiCredentialModel} from '../../../../model/peo-api-credential.model';
import {PeoService} from '../../../../service/peo.service';
import {
  MatDialogContent,
  MatDialogTitle,
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogRef
} from '@angular/material/dialog';
import {MatError, MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {NgIf} from '@angular/common';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {PeoApiCredentialTestModel} from '../../../../model/peo-api-credential-test.model';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {UtilityService} from '../../../../service/utility.service';
import {MatSnackBar, MatSnackBarRef, TextOnlySnackBar} from '@angular/material/snack-bar';

/**
 * @class
 * @description This class maintains the logic for the administrator PEO API Credential dialog component.
 * @export
 * @name PeoApiCredentialsAdminDialogComponent
 */
@Component({
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    NgIf,
    MatIconButton,
    MatIcon,
    MatSuffix,
    MatTooltip,
    MatDialogActions,
    MatButton,
    MatProgressSpinner,
  ],
  selector: 'app-dialog-admin-peo-api-credentials',
  standalone: true,
  styleUrl: './peo-api-credentials.component.scss',
  templateUrl: './peo-api-credentials.component.html'
})
export class PeoApiCredentialsAdminDialogComponent implements AfterViewInit {

  /**
   * @description This property contains the form group for the PEO API Credentials.
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @name PeoApiCredentialsAdminDialogComponent#form
   * @property
   * @protected
   * @type {FormGroup<{
   *       created: FormControl<Date>,
   *       password: FormControl<string>,
   *       peoId: FormControl<string>,
   *       updated: FormControl<Date>,
   *       url: FormControl<string>,
   *       username: FormControl<string>
   *   }>}
   */
  protected readonly form: FormGroup<{
    created: FormControl<Date>,
    password: FormControl<string>,
    peoId: FormControl<string>,
    updated: FormControl<Date>,
    url: FormControl<string>,
    username: FormControl<string>
  }>;

  /**
   * @default true
   * @description This property contains the form disabled status.
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @name PeoApiCredentialsAdminDialogComponent#formDisabled
   * @property
   * @protected
   * @type {boolean}
   */
  protected formDisabled: boolean = true;

  /**
   * @description This property denotes whether to hide the API password or not.
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @name PeoApiCredentialsAdminDialogComponent#hidePassword
   * @property
   * @protected
   * @type {boolean}
   */
  protected hidePassword: boolean = true;

  /**
   * @description This property contains the default PEO API credentials.
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @name PeoApiCredentialsAdminDialogComponent#peoId
   * @property
   * @protected
   * @type {string}
   */
  protected peoId!: string;

  /**
   * @description This property denotes whether the API credentials have been tested or not.
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @name PeoApiCredentialsAdminDialogComponent#tested
   * @property
   * @protected
   * @protected
   */
  protected tested: boolean = false;

  /**
   * @description This property denotes whether the API credentials have been tested successfully or not.
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @name PeoApiCredentialsAdminDialogComponent#testedSuccessfully
   * @property
   * @protected
   * @type {boolean}
   */
  protected testedSuccessfully: boolean = false;

  /**
   * @description This property denotes whether the API credentials are currently being tested or not.
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @name PeoApiCredentialsAdminDialogComponent#testing
   * @property
   * @protected
   * @type {boolean}
   */
  protected testing: boolean = false;

  /**
   * @constructor
   * @description This method instantiates the Admin PEO API Credentials dialog component.
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @name PeoApiCredentialsAdminDialogComponent#constructor()
   * @param {MatDialogRef<PeoApiCredentialsAdminDialogComponent>} dialogReference This parameter contains the reference to the open dialog.
   * @param {FormBuilder} formBuilder This parameter contains the form builder.
   * @param {PeoService} service This parameter contains the PEO service.
   * @param {{ peoId: string }} data This parameter contains the data for the dialog.
   * @public
   */
  public constructor(
    /**
     * @description This property contains the reference to our dialog.
     * @memberof PeoApiCredentialsAdminDialogComponent
     * @name PeoApiCredentialsAdminDialogComponent#dialogReference
     * @property
     * @protected
     * @readonly
     * @type {MatDialogRef<PeoApiCredentialsAdminDialogComponent>}
     */
    private readonly dialogReference: MatDialogRef<PeoApiCredentialsAdminDialogComponent>,
    /**
     * @description This property contains the form builder.
     * @memberof PeoApiCredentialsAdminDialogComponent
     * @name PeoApiCredentialsAdminDialogComponent#formBuilder
     * @property
     * @protected
     * @readonly
     * @type {FormBuilder}
     */
    private readonly formBuilder: FormBuilder,
    /**
     * @description This property contains the PEO service.
     * @memberof PeoApiCredentialsAdminDialogComponent
     * @name PeoApiCredentialsAdminDialogComponent#service
     * @private
     * @property
     * @readonly
     * @type {PeoService}
     */
    private readonly service: PeoService,
    /**
     * @description This property contains the snack bar service.
     * @memberof PeoApiCredentialsAdminDialogComponent
     * @name PeoApiCredentialsAdminDialogComponent#snackbar
     * @private
     * @property
     * @readonly
     * @type {MatSnackBar}
     */
    private readonly snackbar: MatSnackBar,
    /**
     * @description The data for the dialog.
     * @memberof PageAdminPeoComponent
     * @name PageAdminPeoComponent#data
     * @property
     * @public
     * @type {{ peoId: string }}
     */
    @Inject(MAT_DIALOG_DATA)
    public data: { peoId: string }
  ) {

    // Set the PEO ID into the instance.
    this.peoId = this.data.peoId;

    // Build our form and set it into the instance.
    this.form = this.formBuilder.group({

      // Define our created form control.
      created: new FormControl<Date>(UtilityService.DateTimeUtc(), {

        // Set the form control to be non-nullable.
        nonNullable: true,

        // Add our required validator to the form control.
        validators: [Validators.required]
      }),

      // Define our password form control.
      password: new FormControl<string>('', {

        // Set the form control to be non-nullable.
        nonNullable: true,

        // Add our required validator to the form control.
        validators: [Validators.required]
      }),

      // Define our PEO ID form control.
      peoId: new FormControl<string>('', {

        // Set the form control to be non-nullable.
        nonNullable: true,

        // Add our required validator to the form control.
        validators: [Validators.required]
      }),

      // Define our updated form control.
      updated: new FormControl<Date>(UtilityService.DateTimeUtc(), {

        // Set the form control to be non-nullable.
        nonNullable: true,

        // Add our required validator to the form control.
        validators: [Validators.required]
      }),
      url: new FormControl<string>('', {

        // Set the form control to be non-nullable.
        nonNullable: true,

        // Add our required and url validator to the form control.
        validators: [Validators
          .pattern(/^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/),
          Validators.required]
      }),

      // Define our username form control.
      username: new FormControl<string>('', {

        // Set the form control to be non-nullable.
        nonNullable: true,

        // Add our required validator to the form control.
        validators: [Validators.required]
      })
    });
  }

  /**
   * @description This method converts the form to its model variant.
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @method
   * @name PeoApiCredentialsAdminDialogComponent#formToModel()
   * @private
   * @returns {PeoApiCredentialModel}
   */
  private formToModel(): PeoApiCredentialModel {

    // Define our model instance from the form and return it.
    return this.form.value as PeoApiCredentialModel;
  }

  /**
   * @description This method closes the open dialog.
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @method
   * @name PeoApiCredentialsAdminDialogComponent#cancel()
   * @protected
   * @returns {void}
   */
  protected cancel(): void {

    // Close the open dialog.
    this.dialogReference.close();
  }

  /**
   * @description This method resets the API Credentials form to the original values.
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @method
   * @name PeoApiCredentialsAdminDialogComponent#reset()
   * @param {PeoApiCredentialModel} apiCredentials The API credentials to reset the form to.
   * @protected
   * @returns {void}
   */
  protected reset(apiCredentials: PeoApiCredentialModel): void {

    // Disable the form if it is not already disabled.
    if (!this.formDisabled) {

      // Disable the form.
      this.formDisabled = true;
    }

    // Set the administrator information into the form.
    this.form.patchValue({

      // Patch the created date into the form.
      created: apiCredentials.created,

      // Patch the password into the form.
      password: apiCredentials.password,

      // Patch the PEO ID into the form.
      peoId: apiCredentials.peoId,

      // Patch the updated date into the form.
      updated: apiCredentials.updated,

      // Patch the URL into the form.
      url: apiCredentials.url,

      // Patch the username into the form.
      username: apiCredentials.username
    });

    // Re-enable the form if it was disabled.
    if (this.formDisabled) this.formDisabled = false;
  }

  /**
   * @description This method saves the PEO API credentials.
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @method
   * @name PeoApiCredentialsAdminDialogComponent#save()
   * @protected
   * @returns {void}
   */
  protected save(): void {

    // Ensure the API Credentials have been tested successfully before saving.
    if (!this.testedSuccessfully) return;

    // Disable the form.
    this.formDisabled = true;

    // Commit the API Credentials to persistent storage.
    this.service.saveApiCredentials(this.formToModel()).subscribe({

      // Handle any errors that occur.
      error: (e: Error) => {

        // Display the error message to the user.
        this.snackbar.open(`API Credential Save for ${this.peoId} failed with:  ${e.message}`, 'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom'
        });

        // Re-enable the form.
        this.formDisabled = false;
      },

      // Handle the next value.
      next: (c: PeoApiCredentialModel): void => this.dialogReference.close(c)
    })
  }

  /**
   * @description This method tests the API credentials/
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @method
   * @name PeoApiCredentialsAdminDialogComponent#test()
   * @protected
   * @returns {void}
   */
  protected test(): void {

    // Disable the form.
    this.formDisabled = true;

    // Reset the testing flag into the instance.
    this.testing = true;

    // Make the HTTP request to the .NET backend.
    this.service.testApiCredentials(this.formToModel()).subscribe({

      // Handle any errors that occur.
      error: (e: Error) => {

        // Display the error message to the user.
        this.snackbar.open(`API Credential Test for ${this.peoId} Failed:  ${e.message}`, 'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom'
        });

        // Re-enable the form.
        this.formDisabled = false;

        // Reset the testing flag.
        this.testing = false;
      },

      // Handle the next value.
      next: (result: PeoApiCredentialTestModel): void => {

        // Reset the tested successfully flag into the instance.
        this.testedSuccessfully = result?.session ?? false;

        // Reset the tested flag.
        this.tested = true;

        // Reset the testing flag into the instance.
        this.testing = false;

        // Re-enable the form.
        this.formDisabled = false;

        // Check for a successful test and send the notification.
        if (this.testedSuccessfully) this.snackbar.open(`API Credential Test for ${this.peoId} succeeded`, 'Dismiss', {
          duration: 5000,
          horizontalPosition: 'right',
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom'
        });

        // Otherwise, send the failure notification.
        else this.snackbar.open(`API Credential Test for ${this.peoId} failed`, 'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom'
        });
      }
    });
  }

  /**
   * @description This method toggles the visibility if the password field.
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @method
   * @name PeoApiCredentialsAdminDialogComponent#togglePasswordVisibility()
   * @protected
   * @returns {void}
   */
  protected togglePasswordVisibility(): void {

    // Toggle the hide password flag to the opposite of what it currently is.
    this.hidePassword = !this.hidePassword;
  }

  /**
   * @description This method gets executed after the view has been initialized.
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @method
   * @name PeoApiCredentialsAdminDialogComponent#ngAfterViewInit()
   * @public
   * @returns {void}
   */
  public ngAfterViewInit(): void {

    // Load the API credentials for the specified PEO.
    this.service.loadApiCredential(this.peoId).subscribe({

      // Handle any errors that occur.
      error: (e: Error): MatSnackBarRef<TextOnlySnackBar> =>
        this.snackbar.open(`API Credential Load for ${this.peoId} failed with:  ${e.message}`, 'Dismiss', {
        duration: 5000,
        horizontalPosition: 'end',
        panelClass: ['snackbar-error'],
        verticalPosition: 'bottom'
      }),

      // Handle the next value.
      next: (a: PeoApiCredentialModel | null): void => this.reset(a ?? PeoApiCredentialModel.Factory(this.peoId))
    });
  }
}
